'use client';

/**
 * Portalen inbäddad i admin (Reservdelar) för personalen — då visas bara själva delsökningen, utan portalens meny,
 * varukorg, köpknappar och chatt (ägaren 2026-10-13: "vi kan inte ha det så här"). Läget gäller bara i en ram och
 * bara efter en inlöst engångslänk från admin (/foretag/personal sätter flaggan i sessionStorage); en företagskund
 * kommer aldrig hit och ser alltid portalen som vanligt.
 */
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { toast } from 'sonner';

export const PERSONAL_FLAGGA = 'af-personal-inbaddad';
/**
 * Portalen öppnad i admins fönster "Reservdelar + försäljning" (ägaren 2026-10-15: "när man letar kan man göra detta
 * samtidigt i ett eget fönster"). Då visas köpknapparna igen, men de lägger delen i försäljningen bredvid i stället
 * för i portalens varukorg. Sätts av /foretag/personal när länken från admin bär ?forsaljning=1 — och tas bort när den
 * inte gör det, så att vanliga Reservdelar i admin aldrig får läget.
 */
export const FORSALJNING_FLAGGA = 'af-personal-forsaljning';
const ADMIN = 'https://admin.autofrance.se';

export function arPersonalInbaddad(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.self !== window.top && sessionStorage.getItem(PERSONAL_FLAGGA) === '1'; } catch { return false; }
}

/** Inbäddad som personal OCH i fönstret med försäljningen — bara då går köpknapparna till admin. */
export function arForsaljningInbaddad(): boolean {
  if (!arPersonalInbaddad()) return false;
  try { return sessionStorage.getItem(FORSALJNING_FLAGGA) === '1'; } catch { return false; }
}

/**
 * Skickar delen till försäljningen i admin (fönstret bredvid) i stället för till portalens varukorg. Returnerar false
 * utanför det läget — då lägger anroparen i varukorgen som vanligt, så att företagskunderna aldrig påverkas.
 * Bilen (ktype och regnummer) följer med, så att raden får regnumret och försäljningen får bilen om den saknar en.
 */
export function laggIForsaljningen(d: { product_id: number; antal?: number; namn?: string; ktype?: number | null; regnr?: string | null }): boolean {
  if (!arForsaljningInbaddad()) return false;
  window.parent.postMessage({
    typ: 'af-lagg-i-forsaljning', product_id: d.product_id, antal: Math.max(1, Math.round(d.antal ?? 1)),
    ktype: d.ktype ?? null, regnr: d.regnr ?? null,
  }, ADMIN);
  toast.success(d.namn ? `Lagd i försäljningen: ${d.namn}` : 'Lagd i försäljningen');
  return true;
}

export function usePersonalInbaddad(): boolean {
  // Från första ritningen när personalsidan redan satt klassen (klientnavigering) — annars blinkade portalens meny.
  const [pa, setPa] = useState(() => typeof document !== 'undefined' && document.body.classList.contains('af-personal') && arPersonalInbaddad());
  // Läses om vid varje sidbyte: chatten och layouten monteras redan på /foretag/personal, innan flaggan satts.
  const sokvag = usePathname();
  useEffect(() => {
    const v = arPersonalInbaddad();
    setPa(v);
    document.body.classList.toggle('af-personal', v);
    // Köpknapparna syns bara i fönstret med försäljningen (globals.css).
    document.body.classList.toggle('af-forsaljning', v && arForsaljningInbaddad());
  }, [sokvag]);
  return pa;
}

/**
 * Berättar för admin var i portalen personalen är (sida och sökning), så att "Eget fönster" och "Tillbaka in i admin"
 * öppnar samma bil och samma sökning. Bara till admin.autofrance.se, bara i inbäddat läge.
 */
export function useRapporteraPlats(aktiv: boolean) {
  useEffect(() => {
    if (!aktiv) return;
    let senast = '';
    const t = setInterval(() => {
      const plats = window.location.pathname + window.location.search;
      if (plats !== senast) { senast = plats; window.parent.postMessage({ typ: 'af-plats', plats }, ADMIN); }
    }, 800);
    return () => clearInterval(t);
  }, [aktiv]);
}
